/**
 * Entity Explorer Modal - Type Definitions
 * Supports dual modes: 'import' (workflow/file versions) and 'select-folder' (folder hierarchy)
 */

/**
 * Entity types that can be imported or browsed
 */
export type ExplorerEntityType =
  | 'FOLDER'
  | 'FLEXI_FOLDER'
  | 'WETLABWORKFLOW'
  | 'COMPUTATIONALWORKFLOW'
  | 'TEXTFILE'
  | 'SPREADSHEETFILE';

/**
 * Result entity types (versioned)
 */
export type ExplorerResultEntityType =
  | 'WETLABWORKFLOW_VERSION'
  | 'COMPUTATIONALWORKFLOW_VERSION'
  | 'TEXTFILE_VERSION'
  | 'SPREADSHEETFILE_VERSION';

/**
 * Entity type labels for display
 */
export const ENTITY_TYPE_LABELS: Record<ExplorerEntityType | ExplorerResultEntityType, string> = {
  FOLDER: 'Folder',
  FLEXI_FOLDER: 'Folder',
  WETLABWORKFLOW: 'Wet-Lab Procedure',
  COMPUTATIONALWORKFLOW: 'Computational Pipeline',
  TEXTFILE: 'Text Editor',
  SPREADSHEETFILE: 'Spreadsheet',
  WETLABWORKFLOW_VERSION: 'Wet-Lab Procedure',
  COMPUTATIONALWORKFLOW_VERSION: 'Computational Pipeline',
  TEXTFILE_VERSION: 'Text Editor',
  SPREADSHEETFILE_VERSION: 'Spreadsheet',
};

/**
 * Configuration object for opening the entity explorer modal
 * This single object controls all behavior - no separate @Input() flags
 */
export interface EntityExplorerConfig {
  /** Mode: 'import' = pick version(s); 'select-folder' = pick destination folder */
  mode: 'import' | 'select-folder';

  /** Entity types to show/import; folders always shown */
  entityTypes: ExplorerEntityType[];

  /** Allow selecting multiple versions (checkbox mode); default false */
  multiple?: boolean;

  /** Custom modal title; auto-derived if omitted */
  title?: string;

  /** Include archived items; default true */
  showArchived?: boolean;

  /** Skip audit logging; default false */
  skipAudit?: boolean;

  /** Hide the caller's own version (e.g., AddTemplate parity) */
  excludeVersionId?: string;

  /** Enable search field; default true */
  enableSearch?: boolean;

  /** Show "keep permissions" checkbox (select-folder mode with isMoveOperation) */
  isMoveOperation?: boolean;

  /** Extensibility hook; v1 supports only 'notebook' */
  source?: 'notebook';

  /** Filter entities by lifecycle canLinkReference setting (forLinkEntity); default false */
  forLinkEntity?: boolean;
}

/**
 * A single version that was selected for import
 */
export interface ImportedVersionResult {
  /** Version ID */
  _id: string;

  /** Display name for the imported version */
  preFillName: string;

  /** Actual entity/version name (for linkEntity fields) */
  name?: string;

  /** Parent entity ID for linkEntity scenarios (e.g., spreadsheetFile._id) */
  entityId?: string;

  /** Entity type of the result (e.g., TEXTFILE_VERSION, WETLABWORKFLOW_VERSION) */
  entityType: ExplorerResultEntityType;

  /** True if this is a master copy */
  isMasterCopy?: boolean;

  /** True if this is a control copy */
  isControlCopy?: boolean;

  /** For workflows: source experiment/workflow ID */
  fromExperimentId?: string | null;

  /** For workflows: source custom form ID */
  fromCustomFormId?: string | null;

  /** Full raw entity from backend (for host-specific needs) */
  raw: Record<string, unknown>;
}

/**
 * Breadcrumb node in the path
 */
export interface BreadcrumbNode {
  _id: string | null;
  name: string;
  entityType: string;
  node?: ExplorerNode;
}

/**
 * Result when mode='import'
 */
export interface ImportExplorerResult {
  kind: 'import';
  items: ImportedVersionResult[];
}

/**
 * Result when mode='select-folder'
 */
export interface FolderExplorerResult {
  kind: 'folder';
  selectedFolder: Record<string, unknown>;
  folderPath: BreadcrumbNode[];
  keepDirectPermissions?: boolean;
}

/**
 * Union of possible modal results
 */
export type EntityExplorerResult = ImportExplorerResult | FolderExplorerResult;

/**
 * Normalized internal node shape
 * All API endpoints are mapped to this consistent shape
 */
export interface ExplorerNode {
  id: string;
  name: string;
  entityType: string;
  kind: 'folder' | 'entity' | 'version';
  createdBy?: string;
  createdAt?: string;
  raw: Record<string, unknown>;
}
